import {
  DeleteOutlined,
  EditOutlined,
  InfoCircleOutlined,
  LinkOutlined,
  PlusOutlined,
  QuestionCircleOutlined
} from "@ant-design/icons"
import {
  Button,
  Empty,
  Flex,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Tag,
  Tooltip,
  Typography
} from "antd"
import React, { useMemo, useState } from "react"

import { type MilestoneDependencyWriteData } from "../../../../../api/project-milestone-dependencies.services.ts"
import {
  type MilestoneDependency,
  MilestoneDependencyType
} from "../../../../../types/project-milestone-dependency.types.ts"
import { type ProjectMilestone } from "../../../../../types/project-phases.types.tsx"

const { Text, Title } = Typography

const TYPE_INFO: Record<
  MilestoneDependencyType,
  { code: string; label: string; hint: string; explain: (source: string, target: string) => string }
> = {
  [MilestoneDependencyType.FINISH_TO_START]: {
    code: "FS",
    label: "Finish → Start",
    hint: "Source finishes, target starts",
    explain: (source, target) => `${target} can start after ${source} is finished.`
  },
  [MilestoneDependencyType.START_TO_START]: {
    code: "SS",
    label: "Start → Start",
    hint: "Source starts, target starts",
    explain: (source, target) => `${target} can start after ${source} has started.`
  },
  [MilestoneDependencyType.FINISH_TO_FINISH]: {
    code: "FF",
    label: "Finish → Finish",
    hint: "Source finishes, target finishes",
    explain: (source, target) => `${target} can finish after ${source} is finished.`
  },
  [MilestoneDependencyType.START_TO_FINISH]: {
    code: "SF",
    label: "Start → Finish",
    hint: "Source starts, target finishes",
    explain: (source, target) => `${target} can finish after ${source} has started.`
  }
}

const TYPE_OPTIONS = Object.entries(TYPE_INFO).map(([value, info]) => ({
  value: Number(value) as MilestoneDependencyType,
  label: (
    <Flex vertical style={{ padding: "2px 0" }}>
      <Text>
        {info.label} ({info.code})
      </Text>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {info.hint}
      </Text>
    </Flex>
  )
}))

function lagCopy(days: number) {
  if (!days) return null
  return `${days} day${days === 1 ? "" : "s"} lag`
}

const RelationshipLegend = () => (
  <Flex vertical gap={10} style={{ maxWidth: 280 }}>
    <Text strong style={{ color: "#fff" }}>
      Dependency types
    </Text>
    {Object.values(TYPE_INFO).map((info) => (
      <Flex vertical key={info.code}>
        <Text style={{ color: "#fff" }}>
          {info.label} ({info.code})
        </Text>
        <Text style={{ color: "rgba(255,255,255,0.75)", fontSize: 12 }}>
          {info.hint === "Source finishes, target starts" &&
            "The target starts after the source finishes. Example: Demolition finishes → Foundation starts."}
          {info.hint === "Source starts, target starts" &&
            "The target starts once the source has started."}
          {info.hint === "Source finishes, target finishes" &&
            "The target finishes after the source finishes."}
          {info.hint === "Source starts, target finishes" &&
            "The target finishes after the source has started."}
        </Text>
      </Flex>
    ))}
  </Flex>
)

interface MilestoneDependenciesModalProps {
  milestone: ProjectMilestone
  milestones: ProjectMilestone[]
  dependencies: MilestoneDependency[]
  saving: boolean
  onClose: () => void
  onCreate: (data: MilestoneDependencyWriteData) => Promise<boolean>
  onUpdate: (
    id: string,
    data: Partial<MilestoneDependencyWriteData>
  ) => Promise<boolean>
  onDelete: (id: string) => Promise<boolean>
}

export function MilestoneDependenciesModal({
  milestone,
  milestones,
  dependencies,
  saving,
  onClose,
  onCreate,
  onUpdate,
  onDelete
}: MilestoneDependenciesModalProps) {
  const nameOf = (id: string) =>
    milestones.find((m) => m.id === id)?.name || "Unknown milestone"

  const related = useMemo(
    () =>
      dependencies.filter(
        (d) => d.source_milestone === milestone.id || d.target_milestone === milestone.id
      ),
    [dependencies, milestone.id]
  )

  // A milestone that already depends on this one (source → this target) can't
  // be picked again — the backend enforces one relationship per ordered pair,
  // and re-offering it here would only surface that as a raw API error.
  const otherMilestones = useMemo(
    () =>
      milestones.filter(
        (m) =>
          m.id !== milestone.id &&
          !related.some(
            (d) => d.source_milestone === m.id && d.target_milestone === milestone.id
          )
      ),
    [milestones, milestone.id, related]
  )

  const [showAddForm, setShowAddForm] = useState(false)

  const [editingId, setEditingId] = useState<string | null>(null)
  const [editType, setEditType] = useState<MilestoneDependencyType>(
    MilestoneDependencyType.FINISH_TO_START
  )
  const [editLag, setEditLag] = useState<number>(0)

  const [dependsOn, setDependsOn] = useState<string | undefined>(undefined)
  const [addType, setAddType] = useState<MilestoneDependencyType>(
    MilestoneDependencyType.FINISH_TO_START
  )
  const [addLag, setAddLag] = useState<number>(0)

  const startEdit = (dependency: MilestoneDependency) => {
    setEditingId(dependency.id)
    setEditType(dependency.type)
    setEditLag(dependency.lag_days)
  }

  const handleSaveEdit = async () => {
    if (!editingId) return
    const ok = await onUpdate(editingId, { type: editType, lag_days: editLag })
    if (ok) setEditingId(null)
  }

  const openAddForm = () => {
    setDependsOn(undefined)
    setAddType(MilestoneDependencyType.FINISH_TO_START)
    setAddLag(0)
    setShowAddForm(true)
  }

  const handleAdd = async () => {
    if (!dependsOn) return
    const ok = await onCreate({
      source_milestone: dependsOn,
      target_milestone: milestone.id,
      type: addType,
      lag_days: addLag
    })
    if (ok) setShowAddForm(false)
  }

  const addExplanation = dependsOn
    ? TYPE_INFO[addType].explain(nameOf(dependsOn), milestone.name) +
      (addLag ? ` (${lagCopy(addLag)})` : "")
    : null

  return (
    <Modal
      open
      title={
        <Flex vertical gap={0}>
          <Title level={5} style={{ margin: 0 }}>
            Dependencies
          </Title>
          <Text type="secondary" style={{ fontSize: 13, fontWeight: 400 }}>
            {milestone.name}
          </Text>
        </Flex>
      }
      onCancel={onClose}
      footer={null}
      width={560}
    >
      <Flex vertical gap={10} style={{ marginTop: 16, marginBottom: 16 }}>
        {related.length === 0 && !showAddForm && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No dependencies yet"
            style={{ margin: "8px 0 16px" }}
          />
        )}

        {related.map((dependency) => {
          const isEditing = editingId === dependency.id
          const isSource = dependency.source_milestone === milestone.id
          // Phrase from this milestone's point of view: either it depends on
          // the other one, or the other one depends on it.
          const otherName = isSource
            ? nameOf(dependency.target_milestone)
            : nameOf(dependency.source_milestone)

          return (
            <Flex
              key={dependency.id}
              vertical
              gap={6}
              style={{
                border: "1px solid #f0f0f0",
                borderRadius: 8,
                padding: "10px 12px"
              }}
            >
              <Flex align="center" justify="space-between" gap="small">
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {isSource ? "Required for" : "Depends on"}
                </Text>
                {!isEditing && (
                  <Flex gap={4}>
                    <Button
                      size="small"
                      type="text"
                      icon={<EditOutlined />}
                      aria-label="Edit dependency"
                      onClick={() => startEdit(dependency)}
                    />
                    <Popconfirm
                      title="Remove dependency?"
                      description={
                        <span>
                          &quot;{isSource ? milestone.name : otherName}&quot; depends on
                          <br />
                          &quot;{isSource ? otherName : milestone.name}&quot;.
                        </span>
                      }
                      okText="Remove dependency"
                      cancelText="Cancel"
                      okButtonProps={{ danger: true }}
                      onConfirm={() => onDelete(dependency.id)}
                    >
                      <Button
                        size="small"
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        aria-label="Delete dependency"
                      />
                    </Popconfirm>
                  </Flex>
                )}
              </Flex>

              <Flex align="center" gap={6}>
                <LinkOutlined style={{ color: "var(--ant-color-primary)" }} />
                <Text strong ellipsis>
                  {otherName}
                </Text>
              </Flex>

              {isEditing ? (
                <Flex gap="small" align="center" wrap>
                  <Select
                    size="small"
                    style={{ width: 190 }}
                    options={TYPE_OPTIONS.map((o) => ({
                      ...o,
                      label: TYPE_INFO[o.value].label + " (" + TYPE_INFO[o.value].code + ")"
                    }))}
                    value={editType}
                    onChange={setEditType}
                  />
                  <InputNumber
                    size="small"
                    min={0}
                    addonAfter="days"
                    style={{ width: 110 }}
                    value={editLag}
                    onChange={(value) => setEditLag(value ?? 0)}
                  />
                  <Button size="small" type="primary" loading={saving} onClick={handleSaveEdit}>
                    Save
                  </Button>
                  <Button size="small" onClick={() => setEditingId(null)}>
                    Cancel
                  </Button>
                </Flex>
              ) : (
                <Flex align="center" gap={6}>
                  <Tag style={{ marginInlineEnd: 0 }}>{TYPE_INFO[dependency.type].label}</Tag>
                  {dependency.lag_days > 0 && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {lagCopy(dependency.lag_days)}
                    </Text>
                  )}
                </Flex>
              )}
            </Flex>
          )
        })}
      </Flex>

      {showAddForm ? (
        <Flex
          vertical
          gap={12}
          style={{
            borderTop: related.length ? "1px solid #f0f0f0" : "none",
            paddingTop: related.length ? 16 : 0
          }}
        >
          <Flex vertical gap={0}>
            <Text strong>Add dependency</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Set a relationship between two milestones.
            </Text>
          </Flex>

          <Flex vertical gap={4}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Milestone
            </Text>
            <Input value={milestone.name} disabled />
          </Flex>

          <Flex vertical gap={4}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Depends on
            </Text>
            <Select
              showSearch
              allowClear
              placeholder="Select milestone"
              optionFilterProp="label"
              value={dependsOn}
              onChange={setDependsOn}
              options={otherMilestones.map((m) => ({ value: m.id, label: m.name }))}
            />
          </Flex>

          <Flex gap="small">
            <Flex vertical gap={4} style={{ flex: 1 }}>
              <Flex align="center" gap={4}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Relationship
                </Text>
                <Tooltip title={<RelationshipLegend />} color="#1f2937">
                  <QuestionCircleOutlined style={{ fontSize: 12, color: "#8c8c8c" }} />
                </Tooltip>
              </Flex>
              <Select
                options={TYPE_OPTIONS}
                value={addType}
                onChange={setAddType}
                popupMatchSelectWidth={320}
              />
            </Flex>
            <Flex vertical gap={4}>
              <Flex align="center" gap={4}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Lag (optional)
                </Text>
                <Tooltip title="Lag adds a delay between the dependency and the next milestone. For example, 2 days means the dependent milestone starts 2 days after the dependency condition is met.">
                  <QuestionCircleOutlined style={{ fontSize: 12, color: "#8c8c8c" }} />
                </Tooltip>
              </Flex>
              <InputNumber
                min={0}
                addonAfter="days"
                value={addLag}
                onChange={(value) => setAddLag(value ?? 0)}
              />
            </Flex>
          </Flex>

          {addExplanation && (
            <Flex
              align="flex-start"
              gap={8}
              style={{
                background: "#f0f7f6",
                border: "1px solid #d5ebe8",
                borderRadius: 8,
                padding: "8px 10px"
              }}
            >
              <InfoCircleOutlined style={{ color: "var(--ant-color-primary)", marginTop: 2 }} />
              <Text style={{ fontSize: 12.5 }}>{addExplanation}</Text>
            </Flex>
          )}

          <Flex justify="flex-end" gap={8}>
            <Button onClick={() => setShowAddForm(false)}>Cancel</Button>
            <Button type="primary" disabled={!dependsOn} loading={saving} onClick={handleAdd}>
              Add dependency
            </Button>
          </Flex>
        </Flex>
      ) : (
        <Button
          type="dashed"
          block
          icon={<PlusOutlined />}
          disabled={!otherMilestones.length}
          onClick={openAddForm}
          style={{ marginTop: related.length ? 4 : 0 }}
        >
          Add dependency
        </Button>
      )}
    </Modal>
  )
}
